import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TestCard from '../components/common/TestCard';
import EmptyState from '../components/common/EmptyState';
import { TestsShimmer } from '../components/shimmer';
import {
  useGetTestsListQuery,
  useSearchTestsQuery,
  usePrefetch,
} from '../store/edosApi';

const PER_PAGE = 10;

// Map Laravel API row → TestCard's expected shape
const mapApiToCard = (t) => ({
  id: t.id,
  name: t.test_name,
  icon: '🧪',
  tag: t.departments || t.specialisation || t.test_type || null,
  cardTag: t.test_code,
  outcomes: t.parameters
    ? String(t.parameters).split(',').slice(0, 3).map((s) => s.trim()).filter(Boolean)
    : [],
  price: Number(t.mrp) || 0,
  original: null,
});

const Tests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const [q, setQ] = useState(initialQ);
  const [submittedQ, setSubmittedQ] = useState(initialQ); // only triggers search after Enter / button
  const [page, setPage] = useState(1);

  // Re-sync if user lands here from a different ?q= (e.g. clicks two chips in
  // a row from TestFinder). Only fires when the URL param actually changes.
  useEffect(() => {
    const urlQ = searchParams.get('q') || '';
    if (urlQ !== submittedQ) {
      setQ(urlQ);
      setSubmittedQ(urlQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const isSearching = submittedQ.trim().length > 0;

  // Default catalogue.
  //
  // This listed PROFILES, not tests — /get-edos/profiles is the PR-code
  // series, 615 rows, while the catalogue holds ~2,126. So the Tests page
  // showed "615 tests found" and the other 1,500 were reachable only by
  // searching, which hits a different endpoint entirely. Now it lists the
  // catalogue, and `per_page` matches the page's own PER_PAGE so the
  // pagination strip agrees with what is on screen.
  const profilesQuery = useGetTestsListQuery(
    { page, perPage: PER_PAGE },
    { skip: isSearching }
  );

  // Search
  const searchQuery = useSearchTestsQuery(
    { typewords: submittedQ.trim(), page, perPage: PER_PAGE },
    { skip: !isSearching }
  );

  const active = isSearching ? searchQuery : profilesQuery;
  const { data, isLoading, isFetching, isError, error } = active;

  const pagination = data?.pagination ?? null;
  const cards = useMemo(() => (data?.items ?? []).map(mapApiToCard), [data]);

  // Prefetch next page so Pagination feels instant
  const prefetchCatalogue = usePrefetch('getTestsList');
  const prefetchSearch = usePrefetch('searchTests');
  useEffect(() => {
    if (!pagination) return;
    if (pagination.current_page >= pagination.last_page) return;
    if (isSearching) {
      prefetchSearch({ typewords: submittedQ.trim(), page: page + 1, perPage: PER_PAGE });
    } else {
      prefetchCatalogue({ page: page + 1, perPage: PER_PAGE });
    }
  }, [pagination, page, isSearching, submittedQ, prefetchCatalogue, prefetchSearch]);

  // Reset to page 1 when switching between catalogue ↔ search
  useEffect(() => { setPage(1); }, [submittedQ]);

  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    setSubmittedQ(q);
    if (q.trim()) setSearchParams({ q: q.trim() }); else setSearchParams({});
  };
  const onClear = () => {
    setQ('');
    setSubmittedQ('');
    setSearchParams({});
  };

  return (
    <Box>
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          color: '#fff',
          pt: { xs: 10, md: 13 },
          pb: { xs: 5, md: 6 },
        }}
      >
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          {/* Eyebrow */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, mb: 2, lineHeight: 1 }}>
            <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage-l)' }} />
            <Typography
              component="span"
              sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: 'var(--sage-l)', lineHeight: 1,
              }}
            >
              All Tests
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            className="serif"
            sx={{
              fontSize: { xs: 32, sm: 40, md: 52, lg: 60 },
              fontWeight: 500, fontStyle: 'italic', color: '#fff',
              lineHeight: 1.1, mb: 3.5,
            }}
          >
            Find Your Test
          </Typography>

          {/* Search bar */}
          <Box
            component="form"
            onSubmit={onSubmitSearch}
            sx={{
              bgcolor: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '14px',
              p: '6px 6px 6px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              width: '100%',
            }}
          >
            <SearchIcon sx={{ opacity: 0.5, fontSize: 20, color: '#fff', display: 'block', flexShrink: 0 }} />
            <InputBase
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 1700+ tests by name, test code, department, disease…"
              sx={{
                flex: 1, minWidth: 0,
                fontSize: { xs: 13, md: 14 },
                color: '#fff',
                '& input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
              }}
            />
            {isSearching && (
              <Button
                type="button"
                onClick={onClear}
                sx={{
                  flexShrink: 0,
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 12, fontWeight: 600, textTransform: 'none',
                  px: 1.5, py: 0.75,
                  '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                Clear
              </Button>
            )}
            <Button
              type="submit"
              sx={{
                flexShrink: 0,
                bgcolor: 'var(--sage)',
                color: '#fff',
                borderRadius: '10px',
                fontSize: 13, fontWeight: 700, textTransform: 'none',
                px: 2.5, py: 1,
                '&:hover': { bgcolor: 'var(--forest)' },
              }}
            >
              Search
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'var(--cream)', py: { xs: 4, md: 6 }, minHeight: '50vh' }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          {/* Status row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: 'var(--stone)' }}>
              {isLoading
                ? 'Loading tests…'
                : pagination
                  ? `${pagination.total.toLocaleString()} test${pagination.total !== 1 ? 's' : ''} found${isSearching ? ` for "${submittedQ}"` : ''}`
                  : `${cards.length} test${cards.length !== 1 ? 's' : ''}`}
            </Typography>
            {isFetching && !isLoading && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'var(--stone)' }}>
                <CircularProgress size={14} thickness={5} sx={{ color: 'var(--sage)' }} />
                <Typography sx={{ fontSize: 12 }}>Updating…</Typography>
              </Box>
            )}
          </Box>

          {/* First-paint skeleton — mirrors the 4-column card grid so the
              swap to real data doesn't jump the layout. */}
          {isLoading && <TestsShimmer count={12} />}

          {/* Error */}
          {!isLoading && isError && (
            <EmptyState
              icon="⚠"
              title="Couldn't load tests"
              description={error?.data?.message || error?.error || 'Please check your connection and try again.'}
            />
          )}

          {/* Empty */}
          {!isLoading && !isError && cards.length === 0 && (
            <EmptyState
              icon="🔍"
              title={isSearching ? `No results for "${submittedQ}"` : 'No tests available'}
              description={isSearching ? 'Try a different keyword, test code, or condition.' : ''}
            />
          )}

          {/* Cards grid */}
          {!isLoading && !isError && cards.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  },
                  gap: { xs: 2, md: 2.5 },
                  width: '100%',
                  opacity: isFetching ? 0.6 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {cards.map((t) => (
                  <TestCard key={t.id} test={t} />
                ))}
              </Box>

              {/* Pagination */}
              {pagination && pagination.last_page > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 5 }}>
                  <Button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.current_page <= 1 || isFetching}
                    startIcon={<ChevronLeftIcon />}
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid var(--border-l)',
                      color: 'var(--ink)',
                      borderRadius: 100,
                      px: 2.5, py: 1,
                      textTransform: 'none', fontWeight: 600, fontSize: 13,
                      '&:hover': { bgcolor: 'var(--cream-d)' },
                      '&.Mui-disabled': { opacity: 0.4 },
                    }}
                  >
                    Prev
                  </Button>
                  <Typography sx={{ fontWeight: 700, color: 'var(--ink)', fontSize: 13 }}>
                    Page {pagination.current_page} of {pagination.last_page}
                  </Typography>
                  <Button
                    onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                    disabled={pagination.current_page >= pagination.last_page || isFetching}
                    endIcon={<ChevronRightIcon />}
                    sx={{
                      bgcolor: 'var(--forest)',
                      color: '#fff',
                      borderRadius: 100,
                      px: 2.5, py: 1,
                      textTransform: 'none', fontWeight: 700, fontSize: 13,
                      '&:hover': { bgcolor: 'var(--ink)' },
                      '&.Mui-disabled': { opacity: 0.4 },
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Tests;
